import jsPDF from 'jspdf';
import { Message } from './chatService';

class ExportService {
  /**
   * Export chat to text file
   */
  exportToText(messages: Message[], sessionName: string = 'chat'): void {
    try {
      let content = `Chat History - ${sessionName}\n`;
      content += `Exported: ${new Date().toLocaleString()}\n`;
      content += '='.repeat(50) + '\n\n';

      messages.forEach(message => {
        const time = message.timestamp.toLocaleTimeString();
        const role = message.role === 'user' ? 'You' : 'AI Assistant';

        content += `[${time}] ${role}:\n`;
        content += `${message.content}\n\n`;
      });

      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sessionName}_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export to text:', error);
      throw new Error('Failed to export chat history to text file');
    }
  }

  /**
   * Export chat to PDF
   */
  exportToPDF(messages: Message[], sessionName: string = 'chat'): void {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`Chat History - ${sessionName}`, margin, yPosition);
      yPosition += 10;

      // Export date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Exported: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 15;

      // Messages
      messages.forEach(message => {
        const time = message.timestamp.toLocaleTimeString();
        const role = message.role === 'user' ? 'You' : 'AI Assistant';

        // Check if we need a new page
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = margin;
        }

        // Message header
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        if (message.role === 'user') {
          doc.setTextColor(66, 133, 244);
        } else {
          doc.setTextColor(100, 100, 100);
        }
        doc.text(`[${time}] ${role}:`, margin, yPosition);
        yPosition += 7;

        // Message content
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);

        // Clean markdown for PDF
        const cleanContent = this.cleanMarkdown(message.content);
        const lines = doc.splitTextToSize(cleanContent, maxWidth);

        lines.forEach((line: string) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });

        yPosition += 5; // Space between messages
      });

      // Save PDF
      doc.save(`${sessionName}_${Date.now()}.pdf`);
    } catch (error) {
      console.error('Failed to export to PDF:', error);
      throw new Error('Failed to export chat history to PDF');
    }
  }

  /**
   * Clean markdown formatting for plain text export
   */
  private cleanMarkdown(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/\[Button:.*?\]/g, '') // Remove button syntax
      .replace(/^#{1,6}\s+/gm, '') // Remove heading markers
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Convert links to text
      .trim();
  }

  /**
   * Copy chat to clipboard
   */
  async copyToClipboard(messages: Message[]): Promise<void> {
    try {
      let content = '';

      messages.forEach(message => {
        const time = message.timestamp.toLocaleTimeString();
        const role = message.role === 'user' ? 'You' : 'AI Assistant';
        content += `[${time}] ${role}: ${this.cleanMarkdown(message.content)}\n\n`;
      });

      await navigator.clipboard.writeText(content);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      throw new Error('Failed to copy chat to clipboard');
    }
  }
}

export const exportService = new ExportService();
